/* Domovoi · the one Supabase client.

   Every product shares it, so there is one session, one token refresh and one
   realtime socket per page. Pure: importing it does nothing until supabase()
   is first called. */
import { createClient } from '@supabase/supabase-js';
import { API_URL, PUBLISHABLE_KEY, AUTH_STORAGE_KEY } from './config.js';
import { stored } from './session.js';
import { storage } from './storage.js';

var client = null;

/* Offline, an expired access token can't be renewed, and the SDK would spend
   ~30 seconds retrying the renewal before every request. So while the device
   says it has no network, the SDK is handed the stored session with its expiry
   pushed out: requests go straight out and the service worker answers them
   from its cache. The stored copy itself is never changed (anything the SDK
   writes back gets its real expiry restored), and once the network is back the
   real expiry shows and the SDK renews the token as usual. Network failures
   never make the SDK drop a session, so this can't sign anyone out. */
var REAL_EXPIRY = '_dvRealExpiresAt';
function offline() { return typeof navigator !== 'undefined' && navigator.onLine === false; }
var authStorage = {
  getItem: function (k) {
    var v = storage.get(k);
    if (!v || !offline()) return v;
    try {
      var s = JSON.parse(v), now = Math.floor(Date.now() / 1000);
      if (s && s.access_token && s.expires_at && s.expires_at < now + 300) {
        s[REAL_EXPIRY] = s.expires_at;
        s.expires_at = now + 3600;
        return JSON.stringify(s);
      }
    } catch (e) {}
    return v;
  },
  setItem: function (k, v) {
    try {
      var s = JSON.parse(v);
      if (s && s[REAL_EXPIRY]) { s.expires_at = s[REAL_EXPIRY]; delete s[REAL_EXPIRY]; v = JSON.stringify(s); }
    } catch (e) {}
    storage.set(k, v);
  },
  removeItem: function (k) { storage.remove(k); },
};

/* Offline, a request that can't reach the network (and isn't in the service
   worker's cache) fails at once with a 504 instead of a network error. The SDK
   retries network errors three times with backoff (~7 s per read), which kept
   Catered and Curated on "Catching up…" for half a minute with no signal; a 504
   is final for reads and, for sign-in calls, counts as retryable, so it never
   drops a session. Online, nothing changes. */
function offlineAwareFetch(input, init) {
  return fetch(input, init).catch(function (err) {
    if (!offline()) throw err;
    return new Response(JSON.stringify({ message: 'Offline', code: 'OFFLINE' }),
      { status: 504, headers: { 'Content-Type': 'application/json' } });
  });
}

export function supabase() {
  if (!client) client = createClient(API_URL, PUBLISHABLE_KEY, {
    global: { fetch: offlineAwareFetch },
    auth: { storageKey: AUTH_STORAGE_KEY, storage: authStorage },
  });
  return client;
}

/* The signed-in session, offline too. The SDK renews an expired access token
   before handing the session back; with no signal that renewal fails and it
   returns no session, which would drop Catered and Curated into signed-out
   mode and show this browser's local list instead of yours. Offline, the
   stored session is still yours: its reads come from the service worker's
   cache (which keys them by URL, not token), and the SDK renews it as soon as
   the signal is back. Online, this is exactly getSession(). */
export async function currentSession() {
  var res = null;
  try { res = await supabase().auth.getSession(); } catch (e) { res = null; }
  var s = res && res.data && res.data.session;
  if (s) return s;
  var offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  var networkError = res && res.error && /fetch|network|failed/i.test(String(res.error.message || ''));
  if (offline || networkError) {
    var kept = stored();
    if (kept && kept.user && kept.user.id) return kept;
  }
  return null;
}
